import React, { ReactElement } from 'react';
import {
  Animated,
  Easing,
  Keyboard,
  LayoutAnimation,
  Platform,
  StyleSheet,
  Text,
} from 'react-native';
import RootSiblingsManager from 'react-native-root-siblings';

import Spacing from '../theming/Spacing';

interface IProps {
  animationsEnabled?: boolean;
  autoHide?: boolean;
  duration: number;
  marginBottom?: number;
  message: string;
  onHide?: () => void;
  root?: RootSiblingsManager;
}

export default class KitSnackItem extends React.Component<IProps> {
  static defaultProps: Partial<IProps> = {
    animationsEnabled: true,
    autoHide: true,
  };

  //****************************************************************************
  // Class Properties
  //****************************************************************************
  snackOpacityValue = new Animated.Value(0);
  keyboardWillShowListener: any;
  keyboardWillHideListener: any;

  //****************************************************************************
  // State
  //****************************************************************************
  state = {
    keyboardIsShowing: false,
    snackBottomValue: 48,
  };

  //****************************************************************************
  // Methods
  //****************************************************************************
  private _hideSnack = () => {
    Animated.timing(this.snackOpacityValue, {
      toValue: 0,
      duration: 500,
      easing: Easing.linear,
      useNativeDriver: true,
    }).start(() => {
      this.props.root?.destroy();
      this.props.onHide?.();
    });
  };

  private _keyboardWillShow = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    this.setState({ snackBottomValue: 350 });
  };

  private _keyboardWillHide = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    this.setState({ snackBottomValue: 48 });
  };

  //****************************************************************************
  // Lifecycle
  //****************************************************************************
  componentDidMount = (): void => {
    this.keyboardWillShowListener = Keyboard.addListener(
      'keyboardWillShow',
      this._keyboardWillShow
    );
    this.keyboardWillHideListener = Keyboard.addListener(
      'keyboardWillHide',
      this._keyboardWillHide
    );

    if (this.props.animationsEnabled) {
      Animated.timing(this.snackOpacityValue, {
        toValue: 1,
        duration: 225,
        easing: Easing.linear,
        useNativeDriver: true,
      }).start();
    } else {
      this.snackOpacityValue.setValue(1);
    }

    if (this.props.autoHide) {
      setTimeout(this._hideSnack, this.props.duration);
    }
  };

  componentWillUnmount = (): void => {
    this.keyboardWillShowListener = null;
    this.keyboardWillHideListener = null;
  };

  render(): ReactElement {
    const { message, marginBottom } = this.props;
    const snackOpacity = this.snackOpacityValue.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
    });
    const bottom = marginBottom ? marginBottom : this.state.snackBottomValue;

    return (
      <Animated.View
        style={[
          styles.snackWrapper,
          {
            opacity: snackOpacity,
            bottom: bottom || 48,
          },
        ]}
      >
        <Text style={styles.snackText}>{message}</Text>
      </Animated.View>
    );
  }
}

//******************************************************************************
// Styles
//******************************************************************************
const styles = StyleSheet.create({
  snackWrapper: {
    ...Platform.select({
      web: { position: 'fixed' as any },
      default: { position: 'absolute' },
    }),
    left: Spacing.l,
    right: Spacing.l,
    backgroundColor: '#222',
    borderRadius: 8,
    padding: 18,
    justifyContent: 'center',
    flexWrap: 'wrap',
    maxWidth: Platform.OS === 'web' ? 360 : 768,
    elevation: 3,
    ...Platform.select({
      ios: {
        shadowColor: '#000000',
        shadowOpacity: 0.15,
        shadowOffset: { width: 0, height: 3 },
        shadowRadius: 11,
      },
      web: {
        boxShadow: '0px 3px 11px rgba(0, 0, 0, 0.15)',
      },
    }),
  },
  snackText: {
    color: '#fff',
    fontSize: 16,
    width: '100%',
    ...(Platform.OS === 'web' && { fontFamily: 'inherit' }),
  },
});
